interface UseCaseCardProps {
  title: string;
  body: string;
  icon: React.ReactNode;
}

const UseCaseCard: React.FC<UseCaseCardProps> = ({ title, body, icon }) => (
  <article className="rounded-2xl border border-slate-200 bg-white shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all p-8">
    <div className="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center mb-6">
      {icon}
    </div>
    <h3 className="text-xl font-bold text-slate-900 mb-3">{title}</h3>
    <p className="text-slate-700 leading-relaxed">{body}</p>
  </article>
);

// Icons
const SearchIcon = (
  <svg className="w-6 h-6 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

const AlertIcon = (
  <svg className="w-6 h-6 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
  </svg>
);

const ClipboardIcon = (
  <svg className="w-6 h-6 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
  </svg>
);

const ShieldCheckIcon = (
  <svg className="w-6 h-6 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
  </svg>
);

const CubeIcon = (
  <svg className="w-6 h-6 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
  </svg>
);

export const UseCasesSection: React.FC = () => {
  const useCases: UseCaseCardProps[] = [
    {
      title: "Instant Project Search",
      body: "Find the exact clause, spec section, drawing note, or RFI thread, with citations back to the source.",
      icon: SearchIcon
    },
    {
      title: "Risk Signal Detection",
      body: "Surface cost exposure, scope drift, and schedule drivers early, across documents and systems.",
      icon: AlertIcon
    },
    {
      title: "Field-to-Office Intelligence",
      body: "Turn daily reports and inspections into structured insight leadership can act on.",
      icon: ClipboardIcon
    },
    {
      title: "Standards + Compliance",
      body: "Answer compliance questions fast, from standards, permits, and QA/QC requirements.",
      icon: ShieldCheckIcon
    },
    {
      title: "Integrate BIM and CAD",
      body: "Retrieve and view 2D and 3D CAD and Building Information Management data.",
      icon: CubeIcon
    }
  ];

  return (
    <section
      id="use-cases"
      className="use-cases-section section-spacing bg-gradient-to-b from-gray-50 to-white"
    >
      <div className="container mx-auto">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <h2 className="text-4xl font-bold text-slate-900 mb-6 leading-tight">
            Built for infrastructure delivery workflows
          </h2>
          <p className="text-lg text-slate-700 leading-relaxed">
            ARK accelerates execution across the lifecycle, from precon to closeout, by turning scattered project data into governed intelligence teams can use every day.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {useCases.map((useCase) => (
            <UseCaseCard key={useCase.title} {...useCase} />
          ))}
        </div>
      </div>
    </section>
  );
};
